import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    Smartphone, Check, ArrowLeft, Lock, Shield, ExternalLink
} from 'lucide-react'
import Button from '../components/Button'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'
import { PixRecorrenciaModal } from '../components/PixRecorrenciaModal'
import { useEfiCartao } from '../hooks/useEfiCartao'
import {
    calcularDataInicialRecorrencia,
    cicloPagamentoFromPeriodicidade,
    normalizarPeriodicidade,
    obterValorCobrancaPlano,
    PERIODICIDADE_LABELS,
    PERIODICIDADE_SUFIXO_PRECO,
} from '../utils/periodicidadePix'

interface PlanoInfo {
    id: string
    nome: string
    nome_display: string
    preco_mensal: number
    preco_anual?: number | null
    periodicidade?: string | null
    descricao: string | null
}

interface CupomAplicado {
    codigo: string
    desconto: number
    tipo: 'percentual' | 'fixo'
    valorFinal: number
    planoId: string
}

export default function FormaPagamento() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { user } = useAuth()
    const [planoInfo, setPlanoInfo] = useState<PlanoInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [processando, setProcessando] = useState(false)
    const [error, setError] = useState('')
    const [cupomAplicado, setCupomAplicado] = useState<CupomAplicado | null>(null)
    const [pixModalOpen, setPixModalOpen] = useState(false)
    const [metodoPagamento, setMetodoPagamento] = useState<'pix' | 'cartao_link'>('pix')
    const { criarLinkAssinaturaCartao, error: cartaoLinkError } = useEfiCartao()

    const planoId = searchParams.get('plano')
    const codigoCupom = searchParams.get('cupom')
    const periodicidadePlano = planoInfo
        ? normalizarPeriodicidade(planoInfo.periodicidade)
        : 'MENSAL'
    const cartaoDisponivel = periodicidadePlano !== 'SEMANAL'

    useEffect(() => {
        if (planoId) {
            loadPlanoInfo()
        } else {
            navigate('/assinar')
        }
    }, [planoId])

    useEffect(() => {
        if (planoInfo && codigoCupom) {
            validarCupom()
        }
    }, [planoInfo, codigoCupom])

    const loadPlanoInfo = async () => {
        try {
            const { data, error } = await supabase
                .from('planos')
                .select('*')
                .eq('id', planoId)
                .single()

            if (error) {
                throw error
            }

            setPlanoInfo(data)
        } catch (err) {
            setError('Erro ao carregar informações do plano')
            console.error('Erro ao carregar plano:', err)
        } finally {
            setLoading(false)
        }
    }

    const validarCupom = async () => {
        if (!planoInfo || !codigoCupom) return

        try {
            const { data, error } = await supabase
                .rpc('validar_cupom', {
                    p_codigo: codigoCupom.trim().toUpperCase(),
                    p_plano: planoInfo.nome
                })

            if (error) {
                console.error('Erro ao validar cupom:', error)
                return
            }

            if (data.valido) {
                setCupomAplicado({
                    codigo: codigoCupom,
                    desconto: data.desconto,
                    tipo: data.tipo_desconto,
                    valorFinal: data.valor_final,
                    planoId: planoInfo.id
                })
            }
        } catch (err) {
            console.error('Erro ao validar cupom:', err)
        }
    }

    const handleFinalizarPagamento = () => {
        if (!user || !planoInfo) {
            setError('Faça login para continuar')
            return
        }
        setError('')
        if (metodoPagamento === 'cartao_link') {
            if (!cartaoDisponivel) {
                setError(
                    'Cartão de crédito não está disponível para planos semanais. Use PIX Automático ou um plano mensal/anual.'
                )
                return
            }
            void handlePagarCartaoLink()
            return
        }
        setPixModalOpen(true)
    }

    const handlePagarCartaoLink = async () => {
        if (!user || !planoInfo) return

        setProcessando(true)
        setError('')

        try {
            const valor = cupomAplicado?.valorFinal || obterValorCobrancaPlano(planoInfo)
            const periodo = normalizarPeriodicidade(planoInfo.periodicidade)

            // 1) Assinatura pendente primeiro (custom_id na EFI = id da assinatura)
            await ativarAssinaturaEfi(
                {
                    metodo: 'cartao',
                    efiStatus: 'link',
                },
                { somentePendente: true }
            )

            const { data: assinaturaPendente } = await supabase
                .from('assinaturas')
                .select('id')
                .eq('user_id', user.id)
                .eq('plano_id', planoInfo.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()

            if (!assinaturaPendente?.id) {
                throw new Error('Não foi possível criar assinatura pendente')
            }

            // 2) Link EFI com assinaturaId como custom_id (webhook/polling resolvem direto)
            const resultado = await criarLinkAssinaturaCartao({
                valor,
                descricao: `Plano ${planoInfo.nome_display} - Latera`,
                periodicidade: periodo,
                planoId: planoInfo.id,
                assinaturaId: assinaturaPendente.id,
            })

            if (!resultado?.paymentUrl) {
                throw new Error(cartaoLinkError || 'Falha ao gerar link de pagamento EFI')
            }

            // 3) Vincula cobrança EFI + pagamento pendente
            await ativarAssinaturaEfi(
                {
                    metodo: 'cartao',
                    subscriptionId: resultado.subscriptionId,
                    chargeId: resultado.chargeId,
                    efiStatus: resultado.status || 'link',
                },
                { somentePendente: true }
            )

            // 4) Registro EFI local (backup; backend também persiste)
            await supabase.from('assinaturas_efi_cartao').insert({
                user_id: user.id,
                assinatura_id: assinaturaPendente.id,
                plan_id_efi: resultado.planId,
                subscription_id_efi: resultado.subscriptionId,
                charge_id_efi: resultado.chargeId,
                status: resultado.status || 'link',
                periodicidade: resultado.periodicidade,
                valor,
                detalhes: {
                    fluxo: 'link_efi',
                    payment_url: resultado.paymentUrl,
                    webhookConfigured: resultado.webhookConfigured ?? null,
                    assinatura_id: assinaturaPendente.id,
                    ...resultado,
                },
            })

            sessionStorage.setItem(
                'efi_cartao_link_pendente',
                JSON.stringify({
                    chargeId: resultado.chargeId,
                    subscriptionId: resultado.subscriptionId,
                    planoId: planoInfo.id,
                    assinaturaId: assinaturaPendente.id,
                    paymentUrl: resultado.paymentUrl,
                    planoNome: planoInfo.nome_display,
                })
            )

            const qs = new URLSearchParams({ plano: planoInfo.id })
            if (codigoCupom) qs.set('cupom', codigoCupom)
            navigate(`/pagamento/cartao/aguardando?${qs.toString()}`)

            window.open(resultado.paymentUrl, '_blank', 'noopener,noreferrer')
        } catch (err) {
            console.error('Erro link EFI cartão:', err)
            setError(
                err instanceof Error ? err.message : 'Erro ao iniciar pagamento na plataforma EFI'
            )
        } finally {
            setProcessando(false)
        }
    }

    const ativarAssinaturaEfi = async (
        payload: {
            idRec?: string
            txidCobrancaImediata?: string
            metodo?: 'pix' | 'cartao'
            subscriptionId?: number | null
            chargeId?: number | null
            efiStatus?: string | null
        },
        options?: { somentePendente?: boolean }
    ) => {
        if (!user || !planoInfo) return

        const somentePendente = options?.somentePendente === true
        const metodo = payload.metodo || 'pix'
        const idRec = payload.idRec
        const txidCobranca =
            payload.txidCobrancaImediata ||
            (payload.chargeId ? `efi-card-${payload.chargeId}` : undefined)

        try {
            setProcessando(true)

            const periodicidade = normalizarPeriodicidade(planoInfo.periodicidade)
            const valorBase = obterValorCobrancaPlano(planoInfo)
            const valorFinal = cupomAplicado?.valorFinal || valorBase
            const proximoPagamento = calcularDataInicialRecorrencia(periodicidade)
            const cicloPagamento = cicloPagamentoFromPeriodicidade(periodicidade)

            const payloadAtivacao = {
                status: (somentePendente ? 'pendente' : 'ativa') as 'pendente' | 'ativa',
                valor_mensal: valorFinal,
                metodo_pagamento: metodo === 'cartao' ? 'cartao' : 'pix',
                data_proximo_pagamento: proximoPagamento,
                ciclo_pagamento: cicloPagamento,
                observacoes: somentePendente
                    ? 'Aguardando confirmação do pagamento no cartão'
                    : (null as string | null),
                updated_at: new Date().toISOString(),
            }

            const { data: assinaturaExistente } = await supabase
                .from('assinaturas')
                .select('id, status')
                .eq('user_id', user.id)
                .eq('plano_id', planoInfo.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()

            let assinaturaId = assinaturaExistente?.id

            if (assinaturaId) {
                let { error: updateError } = await supabase
                    .from('assinaturas')
                    .update(payloadAtivacao)
                    .eq('id', assinaturaId)

                // Fallback se o banco ainda só aceitar mensal/anual
                if (updateError && /ciclo_pagamento/i.test(updateError.message || '')) {
                    const cicloFallback =
                        cicloPagamento === 'anual' ? 'anual' : 'mensal'
                    ;({ error: updateError } = await supabase
                        .from('assinaturas')
                        .update({
                            ...payloadAtivacao,
                            ciclo_pagamento: cicloFallback,
                            observacoes: `Periodicidade Pix: ${periodicidade}`,
                        })
                        .eq('id', assinaturaId))
                }

                if (updateError) throw updateError
            } else {
                let { data: novaAssinatura, error: insertError } = await supabase
                    .from('assinaturas')
                    .insert({
                        user_id: user.id,
                        plano_id: planoInfo.id,
                        data_inicio: new Date().toISOString(),
                        ...payloadAtivacao,
                    })
                    .select('id')
                    .single()

                if (insertError && /ciclo_pagamento/i.test(insertError.message || '')) {
                    const cicloFallback =
                        cicloPagamento === 'anual' ? 'anual' : 'mensal'
                    ;({ data: novaAssinatura, error: insertError } = await supabase
                        .from('assinaturas')
                        .insert({
                            user_id: user.id,
                            plano_id: planoInfo.id,
                            data_inicio: new Date().toISOString(),
                            ...payloadAtivacao,
                            ciclo_pagamento: cicloFallback,
                            observacoes: `Periodicidade Pix: ${periodicidade}`,
                        })
                        .select('id')
                        .single())
                }

                if (insertError) throw insertError
                if (!novaAssinatura?.id) {
                    throw new Error('Assinatura criada sem ID retornado')
                }
                assinaturaId = novaAssinatura.id
            }

            if (idRec && assinaturaId) {
                const { error: recUpdateError } = await supabase
                    .from('recorrencias_pix')
                    .update({
                        assinatura_id: assinaturaId,
                        status: 'aprovada',
                        updated_at: new Date().toISOString()
                    })
                    .eq('id_rec_efi', idRec)

                if (recUpdateError) {
                    console.warn('Não foi possível vincular recorrência na assinatura:', recUpdateError)
                }
            }

            if (metodo === 'cartao' && assinaturaId && (payload.subscriptionId || payload.chargeId)) {
                const { error: cartaoLinkError } = await supabase
                    .from('assinaturas_efi_cartao')
                    .update({
                        assinatura_id: assinaturaId,
                        status: somentePendente
                            ? payload.efiStatus || 'aguardando'
                            : 'active',
                        updated_at: new Date().toISOString(),
                    })
                    .or(
                        [
                            payload.subscriptionId
                                ? `subscription_id_efi.eq.${payload.subscriptionId}`
                                : null,
                            payload.chargeId ? `charge_id_efi.eq.${payload.chargeId}` : null,
                        ]
                            .filter(Boolean)
                            .join(',')
                    )

                if (cartaoLinkError) {
                    console.warn('Não foi possível vincular assinatura EFI cartão:', cartaoLinkError)
                }
            }

            const { data: corretorData } = await supabase
                .from('corretores')
                .select('id')
                .eq('user_id', user.id)
                .maybeSingle()

            if (somentePendente) {
                if (assinaturaId && txidCobranca && corretorData?.id) {
                    await supabase.from('pagamentos').upsert(
                        {
                            user_id: corretorData.id,
                            assinatura_id: assinaturaId,
                            txid: txidCobranca,
                            valor: valorFinal,
                            status: 'pendente',
                            data_vencimento: new Date().toISOString().split('T')[0],
                            metodo_pagamento: 'cartao',
                            detalhes_pix: {
                                provider: 'efi',
                                origem: 'efi_cartao_aguardando',
                                subscriptionId: payload.subscriptionId || null,
                                chargeId: payload.chargeId || null,
                                efiStatus: payload.efiStatus || null,
                            },
                        },
                        { onConflict: 'txid' }
                    )
                }
                return
            }

            const dataPagamento = new Date().toISOString()
            const detalhesPix = {
                provider: 'efi',
                origem:
                    metodo === 'cartao'
                        ? 'efi_cartao_confirmacao'
                        : 'pix_auto_jornada3_confirmacao',
                idRec: idRec || null,
                subscriptionId: payload.subscriptionId || null,
                chargeId: payload.chargeId || null,
                txidCobrancaImediata: txidCobranca || null,
            }

            // 1) Caminho mais confiável: marcar pendentes desta assinatura como pagos
            if (assinaturaId) {
                const { error: updatePendentesError } = await supabase
                    .from('pagamentos')
                    .update({
                        status: 'pago',
                        data_pagamento: dataPagamento,
                        detalhes_pix: detalhesPix,
                        updated_at: dataPagamento
                    })
                    .eq('assinatura_id', assinaturaId)
                    .eq('status', 'pendente')

                if (updatePendentesError) {
                    console.warn('Falha ao atualizar pagamentos pendentes:', updatePendentesError)
                }
            }

            // 2) Garantir registro pago com txid da cobrança imediata (Jornada 3) e/ou idRec
            const txidsParaMarcar = Array.from(
                new Set([txidCobranca, idRec].filter(Boolean) as string[])
            )

            for (const txidPagamento of txidsParaMarcar) {
                const { data: statusAtualizado } = await supabase.rpc('atualizar_status_pagamento', {
                    p_txid: txidPagamento,
                    p_status: 'pago',
                    p_end_to_end_id: null,
                    p_data_pagamento: dataPagamento,
                    p_detalhes_pix: detalhesPix
                })

                if (!statusAtualizado?.sucesso && assinaturaId && corretorData?.id) {
                    const { data: pagamentoCriado } = await supabase.rpc('registrar_pagamento', {
                        p_user_id: corretorData.id,
                        p_assinatura_id: assinaturaId,
                        p_txid: txidPagamento,
                        p_valor: valorFinal,
                        p_data_vencimento: new Date().toISOString().split('T')[0],
                        p_metodo_pagamento: metodo === 'cartao' ? 'cartao' : 'pix',
                        p_recorrencia_id: null,
                        p_detalhes_pix: detalhesPix
                    })

                    if (pagamentoCriado?.sucesso) {
                        await supabase.rpc('atualizar_status_pagamento', {
                            p_txid: txidPagamento,
                            p_status: 'pago',
                            p_end_to_end_id: null,
                            p_data_pagamento: dataPagamento,
                            p_detalhes_pix: detalhesPix
                        })
                    } else {
                        // Fallback direto na tabela pagamentos
                        await supabase
                            .from('pagamentos')
                            .upsert({
                                user_id: corretorData.id,
                                assinatura_id: assinaturaId,
                                txid: txidPagamento,
                                valor: valorFinal,
                                status: 'pago',
                                data_vencimento: new Date().toISOString().split('T')[0],
                                data_pagamento: dataPagamento,
                                metodo_pagamento: metodo === 'cartao' ? 'cartao' : 'pix',
                                detalhes_pix: detalhesPix
                            }, { onConflict: 'txid' })
                    }
                }
            }

            setPixModalOpen(false)

            // Dispara e-mail de confirmação (idempotente no backend)
            if (assinaturaId) {
                try {
                    const backendUrl =
                        import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
                        import.meta.env.VITE_BACKEND_PIX_URL ||
                        'http://localhost:3005'
                    const { data: { session } } = await supabase.auth.getSession()
                    await fetch(`${backendUrl}/api/pix-auto/notificacoes/assinatura-ativada`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(session?.access_token
                                ? { Authorization: `Bearer ${session.access_token}` }
                                : {}),
                        },
                        body: JSON.stringify({
                            assinatura_id: assinaturaId,
                            metodo,
                            email: user.email,
                            planoNome: planoInfo.nome_display || planoInfo.nome,
                            valor: valorFinal,
                            dataProximoPagamento: proximoPagamento,
                            referencia: txidCobranca || undefined,
                            chargeId: payload.chargeId || undefined,
                            txid: idRec ? undefined : txidCobranca,
                            dataPagamento: new Date().toISOString(),
                        }),
                    })
                } catch (notifyErr) {
                    console.warn('Falha ao disparar e-mail de ativação:', notifyErr)
                }
            }

            navigate(`/dashboard?pagamento=sucesso&provider=efi&metodo=${metodo}`)
        } catch (err) {
            console.error('Erro ao ativar assinatura EFI:', err)
            setError(`Erro ao ativar assinatura: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
        } finally {
            setProcessando(false)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    if (!planoInfo) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        Plano não encontrado
                    </h1>
                    <Button onClick={() => navigate('/assinar')}>
                        Voltar para Planos
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="bg-white dark:bg-gray-800 shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate('/assinar')}
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Voltar
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            Forma de pagamento
                        </h1>
                        <div></div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Resumo do Plano */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Resumo do Plano
                        </h2>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Plano:</span>
                                <span className="font-semibold text-gray-900 dark:text-gray-100">
                                    {planoInfo.nome_display}
                                </span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Período:</span>
                                <span className="font-semibold text-gray-900 dark:text-gray-100">
                                    Mensal
                                </span>
                            </div>

                            {cupomAplicado && (
                                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        <span className="font-semibold text-green-800 dark:text-green-400">
                                            Cupom Aplicado
                                        </span>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-green-700 dark:text-green-300">
                                                Cupom de Desconto
                                            </span>
                                            <span className="text-sm font-medium text-green-800 dark:text-green-400">
                                                {cupomAplicado.codigo}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-green-700 dark:text-green-300">
                                                Desconto:
                                            </span>
                                            <span className="text-sm font-medium text-green-800 dark:text-green-400">
                                                -{formatCurrency(cupomAplicado.desconto)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-4">
                                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {cupomAplicado ? 'Total com desconto:' : 'Total:'}
                                </span>
                                <div className="text-right">
                                    {cupomAplicado && (
                                        <div className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                            {formatCurrency(obterValorCobrancaPlano(planoInfo))}
                                        </div>
                                    )}
                                    <span className={`text-2xl font-bold ${cupomAplicado ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                        {formatCurrency(cupomAplicado?.valorFinal || obterValorCobrancaPlano(planoInfo))}
                                    </span>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {PERIODICIDADE_LABELS[normalizarPeriodicidade(planoInfo.periodicidade)]}
                                        {PERIODICIDADE_SUFIXO_PRECO[normalizarPeriodicidade(planoInfo.periodicidade)]}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="flex items-center gap-2 text-green-800 dark:text-green-400">
                                <Check className="w-5 h-5" />
                                <span className="text-sm font-medium">
                                    {(cupomAplicado?.tipo === 'percentual' && cupomAplicado.desconto === 100)
                                        ? 'Período de teste ativo imediatamente'
                                        : metodoPagamento === 'cartao_link'
                                            ? 'Assinatura ativa após confirmação do cartão na EFI'
                                            : 'Assinatura ativa após autorização no app do banco'
                                    }
                                </span>
                            </div>
                            {(cupomAplicado?.tipo === 'percentual' && cupomAplicado.desconto === 100) && (
                                <div className="mt-2 text-xs text-green-700 dark:text-green-300">
                                    Após o período de teste, será cobrado o valor normal do plano
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pagamento */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                            Forma de pagamento
                        </h2>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-red-800 dark:text-red-400">{error}</p>
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                type="button"
                                onClick={() => setMetodoPagamento('pix')}
                                className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                    metodoPagamento === 'pix'
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                                }`}
                            >
                                <div className="flex items-center gap-4">
                                    <Smartphone className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                            PIX Automático
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Cobrança recorrente {PERIODICIDADE_LABELS[periodicidadePlano].toLowerCase()} via débito automático
                                        </p>
                                    </div>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => cartaoDisponivel && setMetodoPagamento('cartao_link')}
                                disabled={!cartaoDisponivel}
                                className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                    !cartaoDisponivel
                                        ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                                        : metodoPagamento === 'cartao_link'
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                                }`}
                            >
                                <div className="flex items-center gap-4">
                                    <ExternalLink className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                            Cartão — Plataforma EFI
                                            <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
                                                Novo
                                            </span>
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {cartaoDisponivel
                                                ? 'Pague na página segura da EFI (recomendado para testes)'
                                                : 'Indisponível para planos semanais (use PIX Automático)'}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-600 dark:text-gray-400 space-y-2">
                            {metodoPagamento === 'pix' ? (
                                <>
                                    <p>1. Confirme seus dados (nome e CPF)</p>
                                    <p>2. Escaneie o QR Code no app do seu banco</p>
                                    <p>3. Autorize o débito automático {PERIODICIDADE_LABELS[periodicidadePlano].toLowerCase()}</p>
                                </>
                            ) : (
                                <>
                                    <p>1. Clique em continuar — abriremos a página de pagamento da EFI</p>
                                    <p>2. Preencha os dados do cartão na plataforma EFI</p>
                                    <p>3. Aguarde a confirmação nesta tela (assinatura fica pendente até aprovar)</p>
                                </>
                            )}
                        </div>

                        <div className="mt-8">
                            <Button
                                variant="btn_add"
                                onClick={handleFinalizarPagamento}
                                loading={processando}
                                disabled={processando}
                                className="w-full"
                            >
                                {processando
                                    ? 'Processando...'
                                    : metodoPagamento === 'cartao_link'
                                        ? 'Pagar na plataforma EFI'
                                        : 'Continuar com PIX Automático'}
                            </Button>
                        </div>

                        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Lock className="w-4 h-4" />
                            <span>Pagamento 100% seguro</span>
                            <Shield className="w-4 h-4 ml-2" />
                        </div>
                    </div>
                </div>
            </div>

            {planoInfo && user && (
                <>
                    <PixRecorrenciaModal
                        isOpen={pixModalOpen}
                        onClose={() => setPixModalOpen(false)}
                        onCancel={() => {
                            setPixModalOpen(false)
                            navigate('/assinar')
                        }}
                        valor={cupomAplicado?.valorFinal || obterValorCobrancaPlano(planoInfo)}
                        descricao={`Plano ${planoInfo.nome_display} - Latera`}
                        planoId={planoInfo.id}
                        userId={user.id}
                        periodicidade={normalizarPeriodicidade(planoInfo.periodicidade)}
                        onRecorrenciaConfirmed={(payload) => {
                            void ativarAssinaturaEfi({
                                ...payload,
                                metodo: 'pix',
                            })
                        }}
                    />
                </>
            )}
        </div>
    )
}
